import { ScopeChangeRequest } from '@/features/admin/scope-change-requests/ScopeChangeRequest'
import { type ScopeChangeRequestStore } from '@/features/admin/scope-change-requests/ScopeChangeRequestStore'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, computed, observable } from 'mobx'
import { type ScopeChangeRequestRejectDialogViewModel } from '@/features/admin/scope-change-requests/ScopeChangeRequestRejectDialog/ScopeChangeRequestRejectDialogViewModel'
import { task } from 'mobx-task'
import type { AlertErrorHandlerFactory } from '@/features/shell'

/**
 * How an admin resolves a change request on behalf of the client.
 */
export type ScopeChangeRequestResolveAction = 'Accept' | 'Reject' | 'None'

export class ScopeChangeRequestResolveDialogViewModel {
  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  @observable accessor scopeChangeRequest = ScopeChangeRequest.none
  @observable accessor resolveAction: ScopeChangeRequestResolveAction = 'None'

  constructor(
    private readonly _scopeChangeRequestStore: ScopeChangeRequestStore,
    private readonly _rejectOnBehalfOfClientDialogVm: ScopeChangeRequestRejectDialogViewModel,
    private readonly _refreshStatusFilter: () => Promise<void>,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Checks if the job for the scope change request to be resolved is closed.
   */
  @computed
  get isJobClosed() {
    return this.scopeChangeRequest.job.status === 'Closed'
  }

  /**
   * Sets the action to perform
   * @param action
   */
  @action.bound
  setAction(action: ScopeChangeRequestResolveAction) {
    this.resolveAction = action
  }

  /**
   * Opens the dialog
   * @param scopeChangeRequest
   */
  @action.bound
  open(scopeChangeRequest: ScopeChangeRequest) {
    this.scopeChangeRequest = scopeChangeRequest
    this.modal.show()
  }

  /**
   * Resets the modal.
   */
  @action.bound
  reset() {
    this.setAction('None')
  }

  /**
   * Resolve the scope change request and close the dialog
   */
  resolve = task.resolved(async () => {
    switch (this.resolveAction) {
      case 'Accept':
        await this._scopeChangeRequestStore
          .acceptScopeChangeRequestOnBehalfOfClient(this.scopeChangeRequest)
          .catch(
            this._createAlertErrorHandler(
              'Accepting the change request failed',
            ),
          )
        await this._refreshStatusFilter()

        return this.modal.close()
      case 'Reject':
        this._rejectOnBehalfOfClientDialogVm.open(this.scopeChangeRequest)
        return this.modal.close()
      case 'None':
        return
    }
  })
}
